import { create } from 'zustand';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export interface Alert {
  id: string;
  message: string;
  type: AlertType;
  details?: string[];
  isVisible: boolean;
  createdAt: number;
}

interface AlertState {
  alerts: Alert[];
  
  // Actions
  showAlert: (message: string, type: AlertType, details?: string[]) => void;
  hideAlert: (id: string) => void;
  removeAlert: (id: string) => void;
  
  // Utility functions
  success: (message: string, details?: string[]) => void;
  error: (message: string, details?: string[]) => void;
  warning: (message: string, details?: string[]) => void;
  info: (message: string, details?: string[]) => void;
}

// Auto-dismiss timeout in milliseconds
const AUTO_DISMISS_TIMEOUT = 5000;

// Maximum number of alerts to show at once
const MAX_ALERTS = 3;

const useAlertStore = create<AlertState>((set, get) => {
  // Map to store the auto-dismiss timeouts
  const timeouts: Record<string, { dismiss: NodeJS.Timeout, remove: NodeJS.Timeout }> = {};
  
  return {
    // State
    alerts: [],
    
    // Actions
    showAlert: (message, type, details = []) => {
      const id = Date.now().toString();
      const newAlert: Alert = {
        id,
        message,
        type,
        details,
        isVisible: true,
        createdAt: Date.now()
      };
      
      // Add the new alert
      set(state => {
        // Create a new array with the new alert
        const newAlerts = [...state.alerts, newAlert];
        
        // Sort by creation time (newest first) and limit to MAX_ALERTS
        return { alerts: newAlerts.sort((a, b) => b.createdAt - a.createdAt).slice(0, MAX_ALERTS) };
      });
      
      // Set auto-dismiss timeout
      timeouts[id] = {
        dismiss: setTimeout(() => {
          set(state => ({
            alerts: state.alerts.map(alert => 
              alert.id === id ? { ...alert, isVisible: false } : alert
            )
          }));
          
          // Set timeout to remove the alert after the animation
          timeouts[id].remove = setTimeout(() => {
            get().removeAlert(id);
          }, 300); // Animation duration
        }, AUTO_DISMISS_TIMEOUT),
        
        remove: setTimeout(() => {}, 0) // Placeholder, will be replaced in the dismiss timeout
      };
    },
    
    hideAlert: (id) => {
      // Clear existing timeouts
      if (timeouts[id]) {
        clearTimeout(timeouts[id].dismiss);
        clearTimeout(timeouts[id].remove);
      }
      
      // Hide the alert
      set(state => ({
        alerts: state.alerts.map(alert => 
          alert.id === id ? { ...alert, isVisible: false } : alert
        )
      }));
      
      // Remove after animation
      timeouts[id] = {
        ...timeouts[id],
        remove: setTimeout(() => {
          get().removeAlert(id);
        }, 300) // Animation duration
      };
    },
    
    removeAlert: (id) => {
      set(state => ({
        alerts: state.alerts.filter(alert => alert.id !== id)
      }));
      
      // Clean up timeouts
      if (timeouts[id]) {
        clearTimeout(timeouts[id].dismiss);
        clearTimeout(timeouts[id].remove);
        delete timeouts[id];
      }
    },
    
    // Utility functions for common alert types
    success: (message, details) => {
      get().showAlert(message, 'success', details);
    },
    
    error: (message, details) => {
      get().showAlert(message, 'error', details);
    },
    
    warning: (message, details) => {
      get().showAlert(message, 'warning', details);
    },
    
    info: (message, details) => {
      get().showAlert(message, 'info', details);
    },
  };
});

export default useAlertStore; 